<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 super-page-kicker">Configuration</p>
          <h2 class="h3 mb-0">Service Providers</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-end">
            <div class="col-lg-7 mb-3 mb-lg-0">
              <p class="super-page-kicker mb-2">Operations</p>
              <h3 class="mb-2">Manage provider accounts and user access</h3>
              <p class="text-muted mb-0">
                Review provider status, monitor configured users, and jump straight into editing.
              </p>
            </div>
            <div class="col-lg-5">
              <div class="row align-items-end">
                <div class="col-md-8 mb-3 mb-md-0">
                  <label class="form-control-label">Search</label>
                  <b-form-input v-model.trim="searchTerm" placeholder="Provider name"></b-form-input>
                </div>
                <div class="col-md-4 text-md-right">
                  <router-link :to="{ name: 'editServiceProvider' }" class="btn btn-primary btn-block">
                    Add Provider
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border-0">
        <div class="card-header bg-white border-0 py-3">
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="mb-0">Provider List</h3>
            <span class="badge badge-pill badge-primary">{{ filteredItems.length }} visible</span>
          </div>
        </div>

        <div v-if="isBusy" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading service providers...</p>
        </div>

        <div v-else-if="filteredItems.length" class="table-responsive">
          <b-table class="mb-0" striped hover :items="filteredItems" :fields="fields">
            <template slot="index" slot-scope="data">
              {{ data.index + 1 }}
            </template>
            <template slot="providerName" slot-scope="data">
              <div>
                <h4 class="mb-1">{{ data.item.name }}</h4>
                <small class="text-muted">
                  ID: {{ data.item.id }} · {{ (data.item.details || []).length }} configured
                  {{ (data.item.details || []).length === 1 ? 'user' : 'users' }}
                </small>
              </div>
            </template>
            <template slot="meterAllowance" slot-scope="data">
              {{ data.item.lot_count }} / {{ data.item.service_provider_mail_config ? data.item.service_provider_mail_config.meter_allowance : 0 }}
            </template>
            <template slot="status" slot-scope="data">
              <span :class="data.item.status ? 'badge badge-success' : 'badge badge-secondary'">
                {{ data.item.status ? 'Active' : 'Inactive' }}
              </span>
            </template>
            <template slot="vilogi" slot-scope="data">
              <span v-if="!data.item.vilogi_configured" class="badge badge-light"
                v-b-tooltip.hover="'No Vilogi API key configured'">
                Not configured
              </span>
              <span v-else-if="data.item.allow_vilogi_webhook" class="badge badge-success"
                v-b-tooltip.hover="'Configured and accounting sync is enabled'">
                Synced
              </span>
              <span v-else class="badge badge-secondary"
                v-b-tooltip.hover="'Configured, but accounting sync is disabled'">
                Configured (off)
              </span>
            </template>
            <template slot="actions" slot-scope="data">
              <div class="d-flex justify-content-center align-items-center">
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="editItem(data.item)">
                  Edit
                </b-button>
                <b-button size="sm" variant="outline-info" class="mr-2" @click="showSites(data.item)">
                  Sites
                </b-button>
                <b-button
                  size="sm"
                  :variant="data.item.status ? 'outline-danger' : 'outline-success'"
                  @click="changeStatus(data.item)">
                  {{ data.item.status ? 'Disable' : 'Enable' }}
                </b-button>
              </div>
            </template>
          </b-table>
          <div class="card-footer border-0 bg-white">
            <b-pagination
              align="right"
              @input="pageClick"
              :total-rows="pagination.total || 0"
              v-model="currentPage"
              :per-page="pagination.per_page || 10">
            </b-pagination>
          </div>
        </div>

        <div v-else class="text-center py-6">
          <h4 class="mb-2">No service providers found</h4>
          <p class="text-muted mb-0">Try a different search term or add a new provider.</p>
        </div>
      </div>
    </div>

    <!-- Site Preferences Modal -->
    <b-modal ref="sitesModal" :title="sitesModal.title" hide-footer centered size="lg" @hidden="resetSitesModal">
      <div v-if="sitesModal.loading" class="text-center py-5">
        <b-spinner variant="primary" class="mb-3"></b-spinner>
        <p class="text-muted mb-0">Loading site preferences...</p>
      </div>

      <div v-else-if="sitesModal.results.length === 0" class="text-center py-5">
        <h5 class="mb-2">No admin users</h5>
        <p class="text-muted mb-0">This provider has no admin users to check preferences for.</p>
      </div>

      <div v-else>
        <div v-for="entry in sitesModal.results" :key="entry.user_id" class="sites-modal-entry mb-3">
          <div class="d-flex align-items-center mb-2">
            <div class="sites-modal-avatar mr-3">{{ (entry.user_name || 'U').charAt(0).toUpperCase() }}</div>
            <div>
              <h5 class="mb-0">{{ entry.user_name }}</h5>
              <small class="text-muted">User ID: {{ entry.user_id }}</small>
            </div>
            <span v-if="entry.has_preferences" class="badge badge-success ml-auto">{{ entry.sites.length }} site{{ entry.sites.length !== 1 ? 's' : '' }}</span>
            <span v-else class="badge badge-secondary ml-auto">No preferences</span>
          </div>

          <div v-if="entry.has_preferences && entry.sites.length" class="sites-tag-list">
            <span v-for="site in entry.sites" :key="site.id" class="site-tag">
              <strong>{{ site.name }}</strong>
              <span class="text-muted ml-1">{{ site.fullname }}</span>
            </span>
          </div>
          <div v-else class="text-muted small pl-1">
            No sites selected — all views will be empty for this user.
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userData from '@/pages/Common/userData';
import ApiServices from '@/services/api';

export default {
  components: {
    userData
  },
  data() {
    return {
      pagination: {},
      currentPage: 1,
      isBusy: false,
      searchTerm: '',
      items: [],
      sitesModal: {
        title: '',
        loading: false,
        results: []
      },
      fields: [
        { key: 'index', label: 'S.No', sortable: false, class: 'text-center' },
        { key: 'providerName', label: 'Provider', sortable: true },
        { key: 'meterAllowance', label: 'Meter Allowance', sortable: false, class: 'text-center' },
        { key: 'status', label: 'Status', sortable: false, class: 'text-center' },
        { key: 'vilogi', label: 'Vilogi', sortable: false, class: 'text-center' },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ]
    }
  },
  computed: {
    ...mapGetters('serviceProvider', ['serviceProviderList']),
    filteredItems() {
      const searchTerm = this.searchTerm.toLowerCase()
      if (!searchTerm) {
        return this.items
      }
      return this.items.filter((item) => {
        const providerName = (item.name || '').toLowerCase()
        return providerName.includes(searchTerm)
      })
    }
  },
  methods: {
    ...mapActions('serviceProvider', ['SERVICE_PROVIDER_LIST', 'SERVICE_PROVIDER_STATUS_CHANGE']),
    getServiceProviderList(page = this.currentPage) {
      this.isBusy = true
      this.SERVICE_PROVIDER_LIST(page)
        .then(() => {
          this.items = this.serviceProviderList.data || []
          // The API returns a raw Laravel paginator (total/per_page at top level); fall back to fractal's meta.pagination. 2026-10-27
          const list = this.serviceProviderList
          this.pagination = list.meta && list.meta.pagination ? list.meta.pagination : { total: list.total, per_page: list.per_page }
          this.currentPage = page
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    changeStatus(data) {
      const actionLabel = data.status ? 'disable' : 'enable'
      const confirmed = window.confirm(`Do you want to ${actionLabel} ${data.name}?`)

      if (!confirmed) {
        return
      }

      this.SERVICE_PROVIDER_STATUS_CHANGE({ service_provider_id: data.id })
        .then(() => {
          this.getServiceProviderList(this.currentPage)
        })
        .catch(() => {})
    },
    editItem(data) {
      this.$router.push({ name: 'editServiceProvider', params: { provider: data } })
    },
    pageClick(page) {
      this.getServiceProviderList(page)
    },
    showSites(provider) {
      const adminUsers = (provider.details || []).filter(u => Number(u.role_id) === 2)
      this.sitesModal.title = `Site Preferences — ${provider.name}`
      this.sitesModal.results = []
      this.sitesModal.loading = true
      this.$refs.sitesModal.show()

      if (adminUsers.length === 0) {
        this.sitesModal.loading = false
        return
      }

      Promise.all(
        adminUsers.map(user =>
          ApiServices.getSuperAdminUserSitePreferences(user.id)
            .then(res => res.data)
            .catch(() => ({
              user_id: user.id,
              user_name: user.name || user.email,
              has_preferences: false,
              sites: []
            }))
        )
      ).then(results => {
        this.sitesModal.results = results
      }).finally(() => {
        this.sitesModal.loading = false
      })
    },
    resetSitesModal() {
      this.sitesModal.title = ''
      this.sitesModal.results = []
      this.sitesModal.loading = false
    }
  },
  created() {
    this.getServiceProviderList(1)
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}

.sites-modal-entry {
  padding: 1rem;
  border: 1px solid #e7ebf4;
  border-radius: 0.85rem;
}

.sites-modal-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #eef2ff;
  color: #3d55df;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.sites-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.site-tag {
  display: inline-block;
  padding: 0.25rem 0.65rem;
  background: #eef2ff;
  border-radius: 999px;
  font-size: 0.8rem;
  color: #3d55df;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/serviceProvider.vue